<template>
<div class="tk-col">
    <!-- SLA -->
    <div class="card p-1 sla-card">
    <div class="d-flex align-items-center justify-content-between mb-1">
        <strong>Tiempo SLA</strong>
        <b-badge pill :variant="priorityBadge">
        Prioridad: {{ slaPriorityLabel }}
        </b-badge>
    </div>

    <div class="sla-clock" :class="slaLevelClass">
        <svg class="sla-analog ml-1" viewBox="0 0 40 40" :class="{ 'sla-analog--run': slaRunning }">
        <circle cx="20" cy="20" r="18" class="sla-analog-face" />
        <circle cx="20" cy="20" r="18" class="sla-analog-ring" />
        <line x1="20" y1="4" x2="20" y2="8" class="sla-analog-tick" />
        <line x1="36" y1="20" x2="32" y2="20" class="sla-analog-tick" />
        <line x1="20" y1="36" x2="20" y2="32" class="sla-analog-tick" />
        <line x1="4" y1="20" x2="8" y2="20" class="sla-analog-tick" />
        <line x1="20" y1="20" x2="20" y2="11" class="sla-analog-hand sla-analog-hour" />
        <line x1="20" y1="20" x2="28" y2="20" class="sla-analog-hand sla-analog-min" />
        <circle cx="20" cy="20" r="1.6" class="sla-analog-center" />
        </svg>
        <span class="sla-time">{{ slaFormatted }}</span>
    </div>

    <div class="sla-bar mt-1">
        <div class="sla-bar-fill" :class="slaLevelClass" :style="{ width: slaPercent + '%' }"></div>
    </div>

    <div class="d-flex justify-content-between mt-50">
        <small class="text-muted">{{ slaRunning ? "En curso" : "Detenido" }}</small>
        <small class="text-muted">{{ slaPercent }}% restante</small>
    </div>

    <div v-if="slaExpired" class="text-danger small font-weight-bold mt-50">
        ¡Tiempo de SLA agotado!
    </div>
    </div>

    <!-- Cambio de estado -->
    <div v-if="statusActions.length">
    <b-form-group label="Actualización estado del ticket" label-for="status-action" class="mb-1">
        <v-select
        id="status-action"
        v-model="statusForm.action"
        :options="statusActions"
        :reduce="(o) => o.value"
        label="label"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        />
    </b-form-group>

    <b-form-group
        :label="selectedAction && selectedAction.requiresComment ? 'Motivo' : 'Descripción / Nota'"
        label-for="status-comment"
        class="mb-1"
    >
        <counted-textarea
        id="status-comment"
        v-model="statusForm.comment"
        rows="3"
        max-rows="6"
        placeholder="Escribe un comentario..."
        :maxlength="textLimits.comment"
        />
    </b-form-group>

    <!-- Iniciar validación / Rechazar solución: evidencia obligatoria (queda como comentario) -->
    <template v-if="requiresEvidence">
        <small class="d-block text-muted mb-50">Evidencia obligatoria: al menos un archivo (foto, video o documento)</small>
        <file-upload-with-preview
        id="status_evidence_attachments"
        :key="'evidence-' + solveUploaderKey"
        name="status_evidence_attachments"
        :label="$t('helpdesk.ticket.attachments')"
        :multiple="true"
        :disabled="changingStatus"
        :uploading="changingStatus"
        :on-update-files="onUpdateSolveFiles"
        accepted-types="Imágenes, videos, PDF, Word, Excel, PowerPoint."
        />
    </template>

    <!-- Resolver -->
    <template v-if="isSolving">
        <b-form-group label="Causa raíz" label-for="solve-root-cause" class="mb-1">
        <counted-textarea
            id="solve-root-cause"
            v-model="solveForm.rootCause"
            rows="3"
            max-rows="6"
            placeholder="Describe detalladamente la causa raíz de la falla"
            :maxlength="textLimits.rootCause"
        />
        </b-form-group>

        <b-form-group label="Acción realizada" label-for="solve-action" class="mb-1">
        <v-select
            id="solve-action"
            v-model="solveForm.actionType"
            :options="resolutionActionOptions"
            :reduce="(o) => o.value"
            label="label"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        />
        </b-form-group>

        <template v-if="requiresEquipment">
        <small class="d-block text-muted mb-50">Datos del nuevo equipamiento</small>
        <b-form-group label="Marca" label-for="solve-brand" class="mb-1">
            <b-form-input id="solve-brand" v-model="solveForm.equipment.brand" />
        </b-form-group>
        <b-form-group label="Modelo" label-for="solve-model" class="mb-1">
            <b-form-input id="solve-model" v-model="solveForm.equipment.model" />
        </b-form-group>
        <b-form-group label="Número de serie" label-for="solve-serial" class="mb-1">
            <b-form-input id="solve-serial" v-model="solveForm.equipment.serial_number" />
        </b-form-group>
        <b-form-group label="Descripción (opcional)" label-for="solve-equipment-description" class="mb-1">
            <b-form-input id="solve-equipment-description" v-model="solveForm.equipment.description" />
        </b-form-group>
        </template>

        <small class="d-block text-muted mb-50">
        Evidencia (foto o video): afectación o componente dañado y equipo/PMI operando correctamente
        <span v-if="hasPhotoEvidence">(el ticket ya tiene fotos o videos)</span>
        </small>
        <file-upload-with-preview
        id="solve_attachments"
        :key="solveUploaderKey"
        name="solve_attachments"
        :label="$t('helpdesk.ticket.attachments')"
        :multiple="true"
        :disabled="changingStatus"
        :uploading="changingStatus"
        :on-update-files="onUpdateSolveFiles"
        accepted-types="Imágenes, videos, PDF, Word, Excel, PowerPoint."
        />
    </template>

    <b-form-group v-if="canChooseResponsible" label="Responsable" label-for="status-responsible" class="mb-1">
        <v-select
        id="status-responsible"
        v-model="statusForm.responsible"
        :options="responsibleOptions"
        :reduce="(o) => o.value"
        label="label"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        />
    </b-form-group>

    <div class="d-flex justify-content-end">
        <b-button variant="primary" :disabled="!canSubmitStatus" @click="changeStatus">
        Cambiar status
        </b-button>
    </div>
    </div>

    <!-- Historial de adjuntos agrupado: Creación / Comentarios / Resolución -->
    <div v-if="evidenceGroups.length" class="res-wrapper">
    <strong class="d-block mb-1">Historial de adjuntos</strong>
    <div
        v-for="g in evidenceGroups"
        :key="'grp-' + g.key"
        class="res-group"
        :class="'res-group--' + g.key"
    >
        <div class="res-group-title">
        <span class="res-dot" :class="'res-dot--' + g.key"></span>
        {{ g.title }}
        <span class="res-count">({{ g.items.length }})</span>
        </div>
        <div class="res-tags">
        <!-- Imágenes: abren en un modal -->
        <button
            v-for="ev in g.items"
            v-if="isImage(ev)"
            :key="'img-' + ev.id"
            type="button"
            class="res-tag res-tag--img"
            :title="ev.original_name"
            @click="openImage(ev)"
        >
            <img :src="ev.url" :alt="ev.original_name" />
        </button>

        <!-- Otros archivos: descarga -->
        <a
            v-for="ev in g.items"
            v-if="!isImage(ev)"
            :key="'file-' + ev.id"
            :href="ev.download_url || ev.url"
            :download="ev.original_name"
            target="_blank"
            rel="noopener"
            class="res-tag"
            :title="ev.original_name"
        >
            <feather-icon icon="DownloadIcon" size="14" class="mr-25" />
            <span class="res-tag-name">{{ ev.original_name }}</span>
        </a>
        </div>
    </div>
    </div>

    <!-- Modal para ver la imagen en grande -->
    <b-modal
    :id="imageModalId"
    hide-footer
    centered
    size="lg"
    :title="previewImage ? previewImage.original_name : ''"
    >
    <div class="text-center">
        <img
        v-if="previewImage"
        :src="previewImage.url"
        :alt="previewImage.original_name"
        class="img-fluid res-preview-image"
        />
    </div>
    <div v-if="previewImage" class="d-flex justify-content-end mt-1">
        <b-button
        variant="outline-secondary"
        size="sm"
        :href="previewImage.url"
        target="_blank"
        rel="noopener"
        >
        <feather-icon icon="ExternalLinkIcon" size="14" class="mr-25" />
        Abrir original
        </b-button>
    </div>
    </b-modal>
</div>
</template>

<script>
import { BButton, BBadge, BFormGroup, BFormInput, BModal } from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed, watch, onMounted, onUnmounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import FileUploadWithPreview from "@/components/BuildRiskForm/FileUploadWithPreview.vue";
import store from "@/store";
import CountedTextarea from "@/views/helpdesk/CountedTextarea.vue";
import { TEXT_LIMITS } from "@/views/helpdesk/textLimits";
import { formatSlaDuration } from "@/views/helpdesk/slaFormat";

const PRIORITY_OPTIONS = [
{ value: 1, label: "Muy baja" },
{ value: 2, label: "Baja" },
{ value: 3, label: "Media" },
{ value: 4, label: "Alta" },
{ value: 5, label: "Muy alta" },
{ value: 6, label: "Crítica" },
];

const RESOLUTION_ACTIONS = {
reconfiguration: "Reconfiguración",
partial_replacement: "Reemplazo parcial",
total_replacement: "Sustitución total",
};

export default {
name: "TicketSlaColumn",
components: {
    BButton,
    BBadge,
    BFormGroup,
    CountedTextarea,
    BFormInput,
    BModal,
    vSelect,
    FileUploadWithPreview,
},
props: {
    ticket: { type: Object, required: true },
    raw: { type: Object, default: null },
    sla: { type: Object, default: null },
    ticketId: { type: [Number, String], required: true },
    statusActions: { type: Array, default: () => [] },
    providerOptions: { type: Array, default: () => [] },
},
setup(props, { emit, root }) {
    const toast = useToast();
    const toastResult = (title, ok) =>
    toast({
        component: ToastificationContent,
        props: {
        title,
        icon: ok ? "CheckIcon" : "AlertTriangleIcon",
        variant: ok ? "success" : "danger",
        },
    });

    const priorityLabel = (idv) => {
    const f = PRIORITY_OPTIONS.find((o) => o.value === idv);
    return f ? f.label : idv;
    };

    // ── SLA en vivo ──
    const slaLoadedAt = ref(Date.now());
    const now = ref(Date.now());
    let slaTimer = null;

    const slaRunning = computed(() => {
    const tk = props.raw && props.raw.ticket;
    return !!(tk && tk.sla_clock_running);
    });
    const slaLimitSeconds = computed(() => Number((props.sla && props.sla.limit_seconds) || 0));
    const slaAccumulatedSeconds = computed(() => {
    const acc = Number((props.sla && props.sla.accumulated_seconds) || 0);
    const elapsed = slaRunning.value ? Math.floor((now.value - slaLoadedAt.value) / 1000) : 0;
    return acc + Math.max(0, elapsed);
    });
    const slaRemainingSeconds = computed(() =>
    Math.max(0, slaLimitSeconds.value - slaAccumulatedSeconds.value)
    );
    const slaExpired = computed(
    () => !!props.sla && slaAccumulatedSeconds.value >= slaLimitSeconds.value
    );
    const slaPercent = computed(() => {
    if (!props.sla || !slaLimitSeconds.value) return 100;
    return Math.round((slaRemainingSeconds.value / slaLimitSeconds.value) * 10000) / 100;
    });
    const slaLevelClass = computed(() => {
    if (slaPercent.value > 50) return "sla-ok";
    if (slaPercent.value > 20) return "sla-warn";
    return "sla-danger";
    });
    const slaFormatted = computed(() => formatSlaDuration(slaRemainingSeconds.value));
    const slaPriorityLabel = computed(() => {
    const tk = props.raw && props.raw.ticket;
    if (tk && tk.priority && tk.priority.name) return tk.priority.name;
    return tk ? priorityLabel(tk.id_priority) : "—";
    });
    const priorityBadge = computed(() => {
    const tk = props.raw && props.raw.ticket;
    const lvl = tk && tk.priority ? tk.priority.level : null;
    if (lvl >= 5) return "danger";
    if (lvl >= 3) return "warning";
    if (lvl >= 1) return "success";
    return "secondary";
    });

    // ── Cambio de estado / Resolver ──
    const statusForm = ref({ action: null, comment: "", responsible: null });
    const changingStatus = ref(false);

    const selectedAction = computed(
    () => props.statusActions.find((a) => a.value === statusForm.value.action) || null
    );
    const canChooseResponsible = computed(
    () => !!(selectedAction.value && selectedAction.value.requiresResponsible)
    );
    // Al reasignar no se ofrece el proveedor actual
    const responsibleOptions = computed(() => {
    const currentProviderId = props.ticket && props.ticket.id_provider;
    if (statusForm.value.action !== "reassign" || !currentProviderId) return props.providerOptions;
    return props.providerOptions.filter((o) => Number(o.value) !== Number(currentProviderId));
    });
    const isSolving = computed(() => statusForm.value.action === "solve");
    const requiresEvidence = computed(() => !!(selectedAction.value && selectedAction.value.requiresEvidence));

    const resolutionActionOptions = Object.entries(RESOLUTION_ACTIONS).map(([value, label]) => ({
    value,
    label,
    }));

    const blankSolveForm = () => ({
    rootCause: "",
    actionType: null,
    equipment: { brand: "", model: "", serial_number: "", description: "" },
    });
    const solveForm = ref(blankSolveForm());
    const solveFiles = ref([]);
    const solveUploaderKey = ref(0);
    const onUpdateSolveFiles = (payload) => {
    solveFiles.value = payload.rawFiles || [];
    };
    // Los archivos son de una sola acción: al cambiarla se limpian
    watch(
    () => statusForm.value.action,
    () => {
        solveFiles.value = [];
        solveUploaderKey.value += 1;
    }
    );

    const requiresEquipment = computed(() =>
    ["partial_replacement", "total_replacement"].includes(solveForm.value.actionType)
    );
    const isImage = (ev) => /^image\//.test(ev.type || "");
    // Foto o video cuentan como evidencia de la resolución
    const isVisual = (file) => /^(image|video)\//.test(file.type || "");
    const ticketEvidences = computed(() =>
    ((props.raw && props.raw.ticket && props.raw.ticket.events) || []).flatMap(
        (e) => e.evidences || []
    )
    );
    const hasPhotoEvidence = computed(() => ticketEvidences.value.some(isVisual));

    // ── Historial de adjuntos (agrupado) ──
    const eventsList = computed(() => (props.raw && props.raw.ticket && props.raw.ticket.events) || []);
    const dedupeById = (arr) => {
    const map = new Map();
    arr.forEach((ev) => {
        if (ev && ev.id != null) map.set(ev.id, ev);
    });
    return [...map.values()];
    };

    // Creación: creation_evidences del ticket, o el primer evento (previous_status null).
    const creationEvidences = computed(() => {
    const tk = props.raw && props.raw.ticket;
    const top = (tk && tk.creation_evidences) || [];
    if (top.length) return top;
    const ev =
        eventsList.value.find((e) => e.previous_status === null) ||
        eventsList.value.find((e) => !e.glpi_payload && (e.evidences || []).length);
    return (ev && ev.evidences) || [];
    });

    // Comentarios: evidencias de eventos glpi_payload.type === "comment".
    const commentEvidences = computed(() =>
    dedupeById(
        eventsList.value
        .filter((e) => e.glpi_payload && e.glpi_payload.type === "comment")
        .flatMap((e) => e.evidences || [])
    )
    );

    // Resolución: resolution_evidences del ticket + eventos type "solution".
    const resolutionEvidences = computed(() => {
    const tk = props.raw && props.raw.ticket;
    const top = (tk && tk.resolution_evidences) || [];
    const fromEvents = eventsList.value
        .filter((e) => e.glpi_payload && e.glpi_payload.type === "solution")
        .flatMap((e) => e.evidences || []);
    return dedupeById([...top, ...fromEvents]);
    });

    const evidenceGroups = computed(() => {
    const groups = [];
    if (creationEvidences.value.length)
        groups.push({ key: "creation", title: "Creación del ticket", items: creationEvidences.value });
    if (commentEvidences.value.length)
        groups.push({ key: "comment", title: "Comentarios", items: commentEvidences.value });
    if (resolutionEvidences.value.length)
        groups.push({ key: "resolution", title: "Resolución", items: resolutionEvidences.value });
    return groups;
    });

    const previewImage = ref(null);
    const imageModalId = `sla_image_modal_${props.ticketId}`;
    const openImage = (ev) => {
    previewImage.value = ev;
    root.$bvModal.show(imageModalId);
    };

    const solveFormComplete = computed(() => {
    const f = solveForm.value;
    if (!f.rootCause.trim() || !f.actionType) return false;
    if (requiresEquipment.value) {
        const eq = f.equipment;
        if (!eq.brand.trim() || !eq.model.trim() || !eq.serial_number.trim()) return false;
    }
    return hasPhotoEvidence.value || solveFiles.value.some(isVisual);
    });

    const buildSolveParams = (comment) => {
    const f = solveForm.value;
    const formData = new FormData();
    formData.append("root_cause", f.rootCause.trim());
    formData.append("resolution_action_type", f.actionType);
    if (requiresEquipment.value) {
        Object.entries(f.equipment).forEach(([key, value]) => {
        if (value && value.trim()) formData.append(`equipment[${key}]`, value.trim());
        });
    }
    if (comment) formData.append("comment", comment);
    solveFiles.value.forEach((file) => formData.append("attachments[]", file));
    return formData;
    };

    const canSubmitStatus = computed(() => {
    if (!props.ticket || changingStatus.value || !selectedAction.value) return false;
    if (selectedAction.value.requiresComment && !statusForm.value.comment.trim()) return false;
    if (selectedAction.value.requiresResponsible && !statusForm.value.responsible) return false;
    if (isSolving.value && !solveFormComplete.value) return false;
    if (requiresEvidence.value && !solveFiles.value.length) return false;
    return true;
    });

    const changeStatus = () => {
    const action = selectedAction.value;
    if (!action) return;

    const comment = statusForm.value.comment.trim();
    let params = {};
    if (action.value === "solve") {
        params = buildSolveParams(comment);
    } else if (action.requiresEvidence) {
        params = new FormData();
        if (action.commentField) params.append(action.commentField, comment);
        else if (comment) params.append("comment", comment);
        solveFiles.value.forEach((file) => params.append("attachments[]", file));
    } else {
        if (action.commentField) params[action.commentField] = comment;
        else if (comment) params.comment = comment;
        if (action.requiresResponsible) params.id_provider = statusForm.value.responsible;
    }

    changingStatus.value = true;
    store
        .dispatch("helpdesk/changeTicketStatus", { id: props.ticketId, action: action.value, params })
        .then((response) => {
        toastResult((response && response.message) || "Estado actualizado.", true);
        statusForm.value = { action: null, comment: "", responsible: null };
        solveForm.value = blankSolveForm();
        solveFiles.value = [];
        solveUploaderKey.value += 1;
        emit("reload");
        })
        .catch((error) => {
        toastResult(error?.response?.data?.message || "No se pudo actualizar el estado.", false);
        })
        .finally(() => {
        changingStatus.value = false;
        });
    };

    onMounted(() => {
    slaTimer = setInterval(() => {
        now.value = Date.now();
    }, 1000);
    });
    onUnmounted(() => {
    clearInterval(slaTimer);
    });

    return {
    textLimits: TEXT_LIMITS,
    requiresEvidence,
    responsibleOptions,
    // SLA
    slaRunning,
    slaExpired,
    slaPercent,
    slaLevelClass,
    slaFormatted,
    slaPriorityLabel,
    priorityBadge,
    // estado / resolver
    statusForm,
    selectedAction,
    canChooseResponsible,
    canSubmitStatus,
    changingStatus,
    changeStatus,
    isSolving,
    solveForm,
    resolutionActionOptions,
    requiresEquipment,
    hasPhotoEvidence,
    solveUploaderKey,
    onUpdateSolveFiles,
    // historial de adjuntos (agrupado)
    evidenceGroups,
    isImage,
    openImage,
    previewImage,
    imageModalId,
    };
},
};
</script>

<style scoped>
/* Layout de columna */
.tk-col {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
}
.tk-col > .card {
    margin-bottom: 0;
    min-height: 0;
}

/* ── SLA ── */
.sla-card {
    background: #f9fafb;
    border: 1px solid #eceff3;
    border-radius: 8px;
}
.sla-clock {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px 10px;
    border-radius: 8px;
    font-variant-numeric: tabular-nums;
    transition: color 0.4s ease, background 0.4s ease;
}
.sla-time {
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: 0.5px;
    line-height: 1.2;
    white-space: normal;
    color: #2c2c2c;
}
/* Reloj analógico */
.sla-analog {
    width: 30px;
    height: 30px;
    flex: 0 0 auto;
}
.sla-analog-face {
    fill: #eaf4fb;
}
.sla-analog-ring {
    fill: none;
    stroke: #7fb2d9;
    stroke-width: 2.5;
}
.sla-analog-tick {
    stroke: #9bb9cf;
    stroke-width: 1.4;
    stroke-linecap: round;
}
.sla-analog-hand {
    stroke: #34586f;
    stroke-linecap: round;
    transform-origin: 20px 20px;
}
.sla-analog-hour { stroke-width: 2.4; }
.sla-analog-min  { stroke-width: 1.8; }
.sla-analog-center { fill: #34586f; }
.sla-analog--run .sla-analog-min {
    animation: sla-rotate 6s linear infinite;
}
.sla-analog--run .sla-analog-hour {
    animation: sla-rotate 72s linear infinite;
}
@keyframes sla-rotate {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}

/* Barra tipo vida */
.sla-bar {
    width: 100%;
    height: 50px;
    background: #ffffff;
    border: 2px solid #d7dde3;
    border-radius: 24px;
    padding: 2px;
    overflow: hidden;
}
.sla-bar-fill {
    height: 100%;
    border-radius: 24px;
    transition: width 0.6s ease, background 0.4s ease;
}

/* Niveles verde → amarillo → rojo */
.sla-clock.sla-ok    { color: #2c2c2c; }
.sla-bar-fill.sla-ok { background: #6fcf7f; }

.sla-clock.sla-warn    { color: #2c2c2c; }
.sla-bar-fill.sla-warn { background: #ffc94d; }

.sla-clock.sla-danger    { color: #ea5455; }
.sla-bar-fill.sla-danger { background: #ea5455; }
.sla-clock.sla-danger .sla-time { color: #ea5455; }
.sla-clock.sla-danger .sla-analog-ring { stroke: #ea5455; }

/* Modo oscuro: el contador en blanco (en rojo se queda cuando el SLA está por agotarse) */
.dark-layout .sla-clock.sla-ok,
.dark-layout .sla-clock.sla-warn,
.dark-layout .sla-clock.sla-ok .sla-time,
.dark-layout .sla-clock.sla-warn .sla-time { color: #fff; }

/* ── Historial de adjuntos (agrupado) ── */
.res-wrapper {
    border-top: 1px solid #eceff3;
    padding-top: 10px;
}
.res-group {
    margin-bottom: 12px;
}
.res-group:last-child {
    margin-bottom: 0;
}
.res-group-title {
    display: flex;
    align-items: center;
    font-size: 0.78rem;
    font-weight: 600;
    color: #6e6b7b;
    margin-bottom: 6px;
}
.res-count {
    margin-left: 4px;
    color: #b9b9c3;
    font-weight: 500;
}
.res-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    flex: 0 0 auto;
    background: #b9b9c3;
}
.res-dot--creation { background: #7367f0; }  /* azul */
.res-dot--comment { background: #00cfe8; }   /* cian */
.res-dot--resolution { background: #28c76f; } /* verde */
.res-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.res-tag {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 4px 8px;
    font-size: 0.75rem;
    color: #5e5873;
    background: #f1f1f2;
    border: 1px solid #e0e0e2;
    border-radius: 6px;
    text-decoration: none;
    transition: background 0.2s ease, border-color 0.2s ease;
}
.res-tag:hover {
    background: #e7e7e9;
    border-color: #d4d4d6;
    color: #5e5873;
}
.res-tag-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
}
/* Miniatura de imagen (botón) en gris */
.res-tag--img {
    padding: 3px;
    cursor: zoom-in;
    line-height: 0;
}
.res-tag--img img {
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 4px;
    filter: grayscale(0.15);
}
.res-tag--img:hover img {
    filter: grayscale(0);
}
.res-preview-image {
    max-height: 70vh;
    border-radius: 6px;
}
</style>